import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Search, ArrowLeft, ArrowRight, Eye, CheckCircle, MessageSquare, X, Send, RefreshCw, Phone, Gift, Package, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import complaintService from '../../services/complaintService';
import CreditWalletModal from './CreditWalletModal';
import { formatIstDateTime } from '../../lib/constants';
// Payment wording is shared with the other admin screens — see lib/paymentLabels.
import { getMethodName } from '../../lib/paymentLabels';

const statusClass = (status) => {
  if (status === 'resolved') return 'bg-green-100 text-green-700';
  if (status === 'in_review') return 'bg-yellow-100 text-yellow-700';
  if (status === 'closed') return 'bg-gray-100 text-gray-700';
  return 'bg-orange-100 text-orange-700';
};

const getDisplayName = (complaint) => {
  if (complaint.isGuest) return complaint.guestName || 'Guest';
  if (complaint.user?.firstName) return `${complaint.user.firstName} ${complaint.user.lastName || ''}`.trim();
  return complaint.user?.email || 'Unknown';
};

const getDisplayEmail = (complaint) => {
  if (complaint.isGuest) return complaint.guestEmail || '';
  return complaint.user?.email || '';
};

const getContactNumber = (complaint) => {
  if (complaint.isGuest) return complaint.guestPhone || '';
  return complaint.user?.phone || '';
};

const formatOrderAmount = (amount) => {
  const num = Number(amount);
  if (amount === null || amount === undefined || Number.isNaN(num)) return '—';
  return `₹${num.toFixed(2)}`;
};

const ComplaintManagement = ({ newCount = 0 }) => {
  const [complaints, setComplaints] = useState([]);
  const [highlightActive, setHighlightActive] = useState(false);

  useEffect(() => {
    if (newCount > 0) {
      setHighlightActive(true);
      const t = setTimeout(() => setHighlightActive(false), 3000);
      return () => clearTimeout(t);
    }
  }, [newCount]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalComplaints, setTotalComplaints] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [issueFilter, setIssueFilter] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [replyModal, setReplyModal] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [creditWalletTarget, setCreditWalletTarget] = useState(null);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(true);
  const threadEndRef = useRef(null);
  const dialogRef = useRef(null);
  const ITEMS_PER_PAGE = 15;

  // Always-current fetch params so the stable silentFetch never goes stale
  const fetchParamsRef = useRef({ currentPage, statusFilter, issueFilter });
  fetchParamsRef.current = { currentPage, statusFilter, issueFilter };

  const fetchComplaints = useCallback(async () => {
    setLoading(true);
    const result = await complaintService.getAdminComplaints({
      page: currentPage,
      limit: ITEMS_PER_PAGE,
      status: statusFilter || undefined,
      issueType: issueFilter || undefined,
    });
    if (result.success) {
      setComplaints(result.data.complaints || []);
      setTotalPages(result.data.pagination.pages || 1);
      setTotalComplaints(result.data.pagination.total || 0);
    }
    setLoading(false);
  }, [currentPage, statusFilter, issueFilter]);

  // Silent fetch — no loading spinner, also syncs open reply modal
  const silentFetch = useCallback(async () => {
    const { currentPage: page, statusFilter: status, issueFilter: issue } = fetchParamsRef.current;
    const result = await complaintService.getAdminComplaints({
      page,
      limit: ITEMS_PER_PAGE,
      status: status || undefined,
      issueType: issue || undefined,
    });
    if (!result.success) return;
    const fresh = result.data.complaints || [];
    setComplaints(fresh);
    setTotalPages(result.data.pagination.pages || 1);
    setTotalComplaints(result.data.pagination.total || 0);
    // Sync open reply modal with latest replies from server
    setReplyModal((prev) => {
      if (!prev) return prev;
      const updated = fresh.find((c) => c.id === prev.id);
      if (!updated) return prev;
      const serverIds = new Set((updated.replies || []).map((r) => r.id));
      const localOnly = (prev.replies || []).filter((r) => !serverIds.has(r.id));
      return { ...prev, replies: [...(updated.replies || []), ...localOnly] };
    });
  }, []); // stable — reads params via ref

  const handleManualRefresh = async () => {
    setRefreshing(true);
    await silentFetch();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchComplaints();
  }, [fetchComplaints]);

  // Real-time updates via SSE — re-fetch when any reply is saved
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://bookmyfresh.com/api';
    const es = new EventSource(`${apiUrl}/complaints/stream`);
    es.addEventListener('update', () => silentFetch());
    return () => es.close();
  }, [silentFetch]);

  useEffect(() => {
    if (replyModal) {
      dialogRef.current?.showModal();
      setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } else {
      dialogRef.current?.close();
    }
  }, [replyModal]);

  const handleUpdateStatus = async (complaintId, newStatus) => {
    setActionLoading(complaintId);
    const result = await complaintService.updateComplaintStatus(complaintId, { status: newStatus });
    if (result.success) {
      setComplaints((prev) => prev.map((c) =>
        c.id === complaintId ? { ...c, status: newStatus } : c
      ));
      if (replyModal?.id === complaintId) {
        setReplyModal((prev) => prev ? { ...prev, status: newStatus } : prev);
      }
    }
    setActionLoading(null);
  };

  const openReplyModal = (complaint) => {
    setReplyModal(complaint);
    setReplyText('');
    setOrderDetailsOpen(true);
  };

  const closeReplyModal = () => {
    setReplyModal(null);
    setReplyText('');
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !replyModal) return;
    setSendingReply(true);
    const result = await complaintService.addAdminReply(replyModal.id, replyText.trim());
    if (result.success) {
      const newReply = result.data;
      setReplyModal((prev) => prev ? { ...prev, replies: [...(prev.replies || []), newReply] } : prev);
      setComplaints((prev) => prev.map((c) =>
        c.id === replyModal.id ? { ...c, replies: [...(c.replies || []), newReply] } : c
      ));
      setReplyText('');
      setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
    setSendingReply(false);
  };

  const filteredComplaints = searchQuery
    ? complaints.filter((item) => {
        const q = searchQuery.toLowerCase();
        return (
          getDisplayName(item).toLowerCase().includes(q) ||
          getContactNumber(item).toLowerCase().includes(q) ||
          item.orderReference?.toLowerCase().includes(q) ||
          item.issueType?.toLowerCase().includes(q) ||
          item.description?.toLowerCase().includes(q)
        );
      })
    : complaints;

  const renderTableContent = () => {
    if (loading) return <div className="p-12 text-center text-gray-500">Loading complaints...</div>;
    if (filteredComplaints.length === 0) return <div className="p-12 text-center text-gray-500">No complaints found.</div>;
    return (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-500">User</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-500">Issue</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-500">Order</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-500">Status</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-500">Submitted</th>
              <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-500 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredComplaints.map((complaint, index) => (
              <tr key={complaint.id} className={`hover:bg-gray-50 transition-colors ${highlightActive && index < newCount ? 'bg-orange-50 border-l-4 border-orange-400' : ''}`}>
                <td className="px-4 py-4">
                  <p className="font-medium text-gray-800">{getDisplayName(complaint)}</p>
                  {getDisplayEmail(complaint) && (
                    <p className="text-xs text-gray-500">{getDisplayEmail(complaint)}</p>
                  )}
                  {getContactNumber(complaint) && (
                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                      <Phone className="w-3 h-3" /> {getContactNumber(complaint)}
                    </p>
                  )}
                  {complaint.isGuest && (
                    <span className="inline-block mt-1 text-[10px] font-semibold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Guest</span>
                  )}
                </td>
                <td className="px-4 py-4">
                  <p className="font-medium text-gray-800">{complaint.issueType}</p>
                  <p className="text-xs text-gray-500 line-clamp-2">{complaint.description}</p>
                </td>
                <td className="px-4 py-4 text-sm text-gray-600">{complaint.orderReference || '—'}</td>
                <td className="px-4 py-4">
                  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusClass(complaint.status)}`}>
                    {complaint.status.replace('_', ' ').toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">
                  {formatIstDateTime(complaint.createdAt)}
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="inline-flex h-8 items-center rounded-2xl border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => handleUpdateStatus(complaint.id, 'in_review')}
                      disabled={actionLoading === complaint.id}
                      className="flex items-center gap-1 h-full px-3 text-xs text-gray-600 hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
                    >
                      <Eye className="w-3.5 h-3.5 shrink-0" /> Review
                    </button>
                    <button
                      onClick={() => openReplyModal(complaint)}
                      disabled={actionLoading === complaint.id}
                      className="flex items-center gap-1 h-full px-3 text-xs text-blue-600 hover:bg-blue-50 border-l border-gray-200 whitespace-nowrap disabled:opacity-50"
                    >
                      <MessageSquare className="w-3.5 h-3.5 shrink-0" />
                      Reply
                      {complaint.replies?.length > 0 && (
                        <span className="flex items-center justify-center h-4 min-w-[1rem] px-1 bg-blue-100 text-blue-700 rounded-full text-[9px] font-bold">
                          {complaint.replies.length}
                        </span>
                      )}
                    </button>
                    {complaint.status === 'resolved' || complaint.status === 'closed' ? (
                      <button
                        onClick={() => handleUpdateStatus(complaint.id, 'open')}
                        disabled={actionLoading === complaint.id}
                        className="flex items-center gap-1 h-full px-3 text-xs text-gray-600 hover:bg-gray-50 border-l border-gray-200 whitespace-nowrap disabled:opacity-50"
                      >
                        <RefreshCw className="w-3.5 h-3.5 shrink-0" /> Reopen
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUpdateStatus(complaint.id, 'resolved')}
                        disabled={actionLoading === complaint.id}
                        className="flex items-center gap-1 h-full px-3 text-xs text-green-700 hover:bg-green-50 border-l border-gray-200 whitespace-nowrap disabled:opacity-50"
                      >
                        <CheckCircle className="w-3.5 h-3.5 shrink-0" /> Resolve
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Complaint Management</h1>
          <p className="text-gray-500 text-sm">{totalComplaints} complaints in the system</p>
        </div>
        <button
          onClick={handleManualRefresh}
          disabled={refreshing}
          title="Refresh complaints"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-3xl shadow-sm p-4 mb-6">
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by user, order, issue, or notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setCurrentPage(1); setStatusFilter(e.target.value); }}
            className="border border-gray-200 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
          >
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="in_review">In Review</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <select
            value={issueFilter}
            onChange={(e) => { setCurrentPage(1); setIssueFilter(e.target.value); }}
            className="border border-gray-200 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
          >
            <option value="">All Issue Types</option>
            <option value="Product Damaged">Product Damaged</option>
            <option value="Wrong Item Delivered">Wrong Item Delivered</option>
            <option value="Missing Item">Missing Item</option>
            <option value="Late Delivery">Late Delivery</option>
            <option value="Poor Quality">Poor Quality</option>
            <option value="Packaging Issue">Packaging Issue</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
        {renderTableContent()}
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
        <p>{totalComplaints} total complaint{totalComplaints === 1 ? '' : 's'}</p>
        <div className="inline-flex items-center gap-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage <= 1}
            className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-2 bg-white text-gray-600 disabled:opacity-50"
          >
            <ArrowLeft className="w-4 h-4" /> Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage >= totalPages}
            className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-2 bg-white text-gray-600 disabled:opacity-50"
          >
            Next <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Reply Modal — native <dialog> for accessibility */}
      <dialog
        ref={dialogRef}
        onClose={closeReplyModal}
        className="w-full max-w-lg rounded-3xl shadow-2xl p-0 m-auto max-h-[90vh] flex flex-col backdrop:bg-black/50 border-0 outline-none"
      >
        {replyModal && (
          <>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200 flex-shrink-0">
              <div>
                <h3 className="font-bold text-gray-900">Complaint #{replyModal.id}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{replyModal.issueType}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {!replyModal.isGuest && replyModal.user && (
                  <button
                    onClick={() => setCreditWalletTarget({
                      id: replyModal.user.id,
                      name: getDisplayName(replyModal),
                    })}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
                  >
                    <Gift className="w-3.5 h-3.5" /> Credit BMF Coins
                  </button>
                )}
                {/* Mark Resolved / Reopen */}
                {replyModal.status === 'resolved' || replyModal.status === 'closed' ? (
                  <button
                    onClick={() => handleUpdateStatus(replyModal.id, 'open')}
                    disabled={actionLoading === replyModal.id}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    Reopen
                  </button>
                ) : (
                  <button
                    onClick={() => handleUpdateStatus(replyModal.id, 'resolved')}
                    disabled={actionLoading === replyModal.id}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 hover:bg-green-200 transition-colors disabled:opacity-50"
                  >
                    Mark Resolved
                  </button>
                )}
                {/* Close — available for any non-closed complaint */}
                {replyModal.status !== 'closed' && (
                  <button
                    onClick={() => handleUpdateStatus(replyModal.id, 'closed')}
                    disabled={actionLoading === replyModal.id}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 hover:bg-red-200 transition-colors flex items-center gap-1 disabled:opacity-50"
                  >
                    <Lock className="w-3 h-3" /> Close
                  </button>
                )}
                <button onClick={closeReplyModal} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Complaint Summary — capped so the chat thread always has room */}
            <div className="px-5 py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0 space-y-3 overflow-y-auto max-h-[40%]">
              <div>
                <p className="text-sm text-gray-700">{replyModal.description}</p>
                <p className="text-xs text-gray-400 mt-1">
                  From: {getDisplayName(replyModal)}{replyModal.isGuest ? ' (Guest)' : ''}
                  {getContactNumber(replyModal) && ` · ${getContactNumber(replyModal)}`}
                  {replyModal.createdAt && (
                    ` · ${formatIstDateTime(replyModal.createdAt)}`
                  )}
                </p>
              </div>

              {replyModal.order && (
                <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setOrderDetailsOpen((o) => !o)}
                    className="w-full flex items-center justify-between mb-2 group"
                  >
                    <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1.5">
                      <Package className="w-3.5 h-3.5" /> Order Details
                    </p>
                    {orderDetailsOpen
                      ? <ChevronUp className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                      : <ChevronDown className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                    }
                  </button>
                  {orderDetailsOpen && (
                    <>
                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                        <p><span className="text-gray-400">Order #:</span> {replyModal.order.orderNumber}</p>
                        <p><span className="text-gray-400">Status:</span> {replyModal.order.status}</p>
                        <p><span className="text-gray-400">Amount:</span> {formatOrderAmount(replyModal.order.totalAmount)}</p>
                        <p><span className="text-gray-400">Payment:</span> {getMethodName(replyModal.order.paymentMethod)} ({replyModal.order.paymentStatus})</p>
                        {replyModal.order.deliveryType && (
                          <p><span className="text-gray-400">Delivery:</span> {replyModal.order.deliveryType}</p>
                        )}
                        {replyModal.order.deliverySlot && (
                          <p><span className="text-gray-400">Slot:</span> {replyModal.order.deliverySlot}</p>
                        )}
                      </div>
                      {replyModal.order.items?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1.5">Items Ordered</p>
                          <ul className="space-y-1">
                            {replyModal.order.items.map((item) => (
                              <li key={item.id} className="flex justify-between text-xs text-gray-600">
                                <span>
                                  {item.productName}
                                  {item.variantLabel && <span className="text-gray-400"> ({item.variantLabel})</span>}
                                  <span className="text-gray-400"> × {item.quantity}</span>
                                </span>
                                <span className="text-gray-700">{formatOrderAmount(Number(item.priceAtPurchase) * item.quantity)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              {!replyModal.order && replyModal.orderReference && (
                <p className="text-xs text-gray-500">Order: {replyModal.orderReference}</p>
              )}
              {replyModal.images?.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {replyModal.images.map((src) => (
                    <a key={src} href={src} target="_blank" rel="noopener noreferrer">
                      <img
                        src={src}
                        alt="complaint attachment"
                        className="h-24 w-full object-cover rounded-xl border border-gray-200 hover:opacity-90 transition-opacity"
                      />
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Thread */}
            <div className="flex-1 overflow-y-auto p-5 space-y-3 min-h-[160px]">
              {(!replyModal.replies || replyModal.replies.length === 0) && (
                <p className="text-center text-sm text-gray-400 py-6">No replies yet. Start the conversation below.</p>
              )}
              {(replyModal.replies || []).map((reply) => {
                if (reply.senderType === 'system') {
                  return (
                    <div key={reply.id} className="flex justify-center my-2">
                      <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl shadow-sm text-xs text-amber-900 max-w-[90%]">
                        <div className="w-7 h-7 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-sm font-bold text-sm">
                          ₹
                        </div>
                        <div className="flex flex-col">
                          <span className="font-semibold text-amber-900">{reply.message}</span>
                          <span className="text-[10px] text-amber-600 font-medium">
                            System Transaction · {new Date(reply.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true })}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                }
                const isAdmin = reply.senderType === 'admin';
                const bubbleClass = isAdmin ? 'bg-[#2e4e18] text-white' : 'bg-gray-100 text-gray-800';
                const nameClass = isAdmin ? 'text-green-200' : 'text-gray-500';
                const timeClass = isAdmin ? 'text-green-300' : 'text-gray-400';
                return (
                  <div key={reply.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${bubbleClass}`}>
                      <p className={`text-[10px] font-semibold mb-1 ${nameClass}`}>{reply.senderName}</p>
                      <p className="text-sm">{reply.message}</p>
                      <p className={`text-[10px] mt-1 ${timeClass}`}>
                        {new Date(reply.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={threadEndRef} />
            </div>

            {/* Reply Input */}
            <div className="p-4 border-t border-gray-200 flex-shrink-0">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendReply()}
                  placeholder="Type your reply..."
                  className="flex-1 border border-gray-200 rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
                />
                <button
                  onClick={handleSendReply}
                  disabled={sendingReply || !replyText.trim()}
                  className="bg-[#2e4e18] text-white px-4 py-2.5 rounded-2xl hover:bg-[#3f6b20] transition-colors disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}

        {creditWalletTarget && (
          <CreditWalletModal
            user={creditWalletTarget}
            complaintId={replyModal?.id ?? null}
            onClose={() => setCreditWalletTarget(null)}
            onCredited={(data) => {
              if (data?.systemReply) {
                setReplyModal((prev) =>
                  prev ? { ...prev, replies: [...(prev.replies || []), data.systemReply] } : prev,
                );
              }
            }}
          />
        )}
      </dialog>
    </div>
  );
};

ComplaintManagement.propTypes = { newCount: PropTypes.number };
export default ComplaintManagement;
